import React, { useState, useEffect } from "react";
import { Box, Typography, Stack, Button, Alert, Checkbox, FormControlLabel } from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

const SpecificPeriodPicker = ({ inputValue, selectRuleHandler, rule, ruleType, setOpen }) => {
    const [error, setError] = useState("");

    // Initialize defaults if empty
    useEffect(() => {
        if (!inputValue?.startDate && !inputValue?.endDate) {
            const now = moment();
            const tomorrow = moment().add(1, 'day');

            selectRuleHandler({
                rule,
                ruleType,
                ruleValue: {
                    startDate: now.format(),
                    endDate: tomorrow.format(),
                },
            });
        }
    }, []);

    const handleApply = () => {
        if (!inputValue?.startDate) {
            setError("Please select a start date and time.");
            return;
        }
        if (!inputValue?.endDate) {
            setError("Please select an end date and time.");
            return;
        }

        const start = moment(inputValue.startDate);
        const end = moment(inputValue.endDate);

        if (end.isSameOrBefore(start)) {
            setError("End period must be after start period.");
            return;
        }

        setError("");
        setOpen(false);
    };

    const handle24hrToggle = (e) => {
        if (e.target.checked) {
            const start = moment().startOf('day');
            const end = moment().add(1, 'day').startOf('day');

            selectRuleHandler({
                rule,
                ruleType,
                ruleValue: {
                    startDate: start.format(),
                    endDate: end.format(),
                },
            });
        } else {
            const now = moment();
            const tomorrow = moment().add(1, 'day');

            selectRuleHandler({
                rule,
                ruleType,
                ruleValue: {
                    startDate: now.format(),
                    endDate: tomorrow.format(),
                },
            });
        }
    };

    // Check if current selection matches 24hr window (today 00:00 to tomorrow 00:00)
    const is24hr = (() => {
        if (!inputValue?.startDate || !inputValue?.endDate) return false;
        const start = moment(inputValue.startDate);
        const end = moment(inputValue.endDate);
        const today0 = moment().startOf('day');
        const tomorrow0 = moment().add(1, 'day').startOf('day');

        return start.isSame(today0) && end.isSame(tomorrow0);
    })();

    return (
        <Box
            sx={{ p: 2, minWidth: 320 }}
            onClick={(e) => e.stopPropagation()}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#333' }}>
                    Select Promotion Period
                </Typography>
                <FormControlLabel
                    control={
                        <Checkbox
                            size="small"
                            checked={is24hr}
                            onChange={handle24hrToggle}
                            sx={{ p: 0.5 }}
                        />
                    }
                    label={<Typography variant="caption" sx={{ fontWeight: 'bold' }}>24 hr</Typography>}
                    sx={{ mr: 0 }}
                />
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2, py: 0, '& .MuiAlert-message': { fontSize: '12px' } }}>
                    {error}
                </Alert>
            )}

            <Stack spacing={2.5}>
                {/* Start Period */}
                <Box>
                    <Typography variant="caption" sx={{ mb: 0.5, display: 'block', fontWeight: 'bold', color: '#666' }}>
                        START PERIOD
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="overline" sx={{ display: 'block', fontSize: '10px', mb: 0.5 }}>DATE</Typography>
                            <DatePicker
                                selected={inputValue?.startDate ? moment(inputValue.startDate).toDate() : null}
                                onChange={(date) => {
                                    if (!date) return;
                                    const mDate = moment(date);
                                    const current = inputValue?.startDate ? moment(inputValue.startDate) : moment();
                                    mDate.hours(current.hours());
                                    mDate.minutes(current.minutes());
                                    mDate.seconds(0);
                                    mDate.milliseconds(0);

                                    setError("");
                                    selectRuleHandler({
                                        rule,
                                        ruleType,
                                        ruleValue: {
                                            ...inputValue,
                                            startDate: mDate.format(),
                                        },
                                    });
                                }}
                                dateFormat="MMM dd, yyyy"
                                popperZIndex={5000}
                                popperProps={{ strategy: 'fixed' }}
                                onFocus={(e) => e.stopPropagation()}
                                customInput={
                                    <input
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                            width: "100%",
                                            padding: "10px",
                                            borderRadius: "6px",
                                            border: "1px solid #e0e0e0",
                                            fontSize: "13px",
                                            outline: 'none',
                                            backgroundColor: '#fafafa'
                                        }}
                                    />
                                }
                            />
                        </Box>
                        <Box sx={{ width: '100px' }}>
                            <Typography variant="overline" sx={{ display: 'block', fontSize: '10px', mb: 0.5 }}>TIME</Typography>
                            <DatePicker
                                selected={inputValue?.startDate ? moment(inputValue.startDate).toDate() : null}
                                onChange={(date) => {
                                    if (!date) return;
                                    const mTime = moment(date);
                                    const current = inputValue?.startDate ? moment(inputValue.startDate) : moment();
                                    current.hours(mTime.hours());
                                    current.minutes(mTime.minutes());
                                    current.seconds(0);
                                    current.milliseconds(0);

                                    setError("");
                                    selectRuleHandler({
                                        rule,
                                        ruleType,
                                        ruleValue: {
                                            ...inputValue,
                                            startDate: current.format(),
                                        },
                                    });
                                }}
                                showTimeSelect
                                showTimeSelectOnly
                                timeIntervals={15}
                                timeCaption="Time"
                                dateFormat="HH:mm"
                                popperZIndex={5000}
                                popperProps={{ strategy: 'fixed' }}
                                onFocus={(e) => e.stopPropagation()}
                                customInput={
                                    <input
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                            width: "100%",
                                            padding: "10px",
                                            borderRadius: "6px",
                                            border: "1px solid #e0e0e0",
                                            fontSize: "13px",
                                            outline: 'none',
                                            backgroundColor: '#fafafa'
                                        }}
                                    />
                                }
                            />
                        </Box>
                    </Stack>
                </Box>

                {/* End Period */}
                <Box>
                    <Typography variant="caption" sx={{ mb: 0.5, display: 'block', fontWeight: 'bold', color: '#666' }}>
                        END PERIOD
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="overline" sx={{ display: 'block', fontSize: '10px', mb: 0.5 }}>DATE</Typography>
                            <DatePicker
                                selected={inputValue?.endDate ? moment(inputValue.endDate).toDate() : null}
                                onChange={(date) => {
                                    if (!date) return;
                                    const mDate = moment(date);
                                    const current = inputValue?.endDate ? moment(inputValue.endDate) : moment();
                                    mDate.hours(current.hours());
                                    mDate.minutes(current.minutes());
                                    mDate.seconds(0);
                                    mDate.milliseconds(0);

                                    setError("");
                                    selectRuleHandler({
                                        rule,
                                        ruleType,
                                        ruleValue: {
                                            ...inputValue,
                                            endDate: mDate.format(),
                                        },
                                    });
                                }}
                                dateFormat="MMM dd, yyyy"
                                minDate={inputValue?.startDate ? moment(inputValue.startDate).toDate() : new Date()}
                                popperZIndex={5000}
                                popperProps={{ strategy: 'fixed' }}
                                onFocus={(e) => e.stopPropagation()}
                                customInput={
                                    <input
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                            width: "100%",
                                            padding: "10px",
                                            borderRadius: "6px",
                                            border: "1px solid #e0e0e0",
                                            fontSize: "13px",
                                            outline: 'none',
                                            backgroundColor: '#fafafa'
                                        }}
                                    />
                                }
                            />
                        </Box>
                        <Box sx={{ width: '100px' }}>
                            <Typography variant="overline" sx={{ display: 'block', fontSize: '10px', mb: 0.5 }}>TIME</Typography>
                            <DatePicker
                                selected={inputValue?.endDate ? moment(inputValue.endDate).toDate() : null}
                                onChange={(date) => {
                                    if (!date) return;
                                    const mTime = moment(date);
                                    const current = inputValue?.endDate ? moment(inputValue.endDate) : moment();
                                    current.hours(mTime.hours());
                                    current.minutes(mTime.minutes());
                                    current.seconds(0);
                                    current.milliseconds(0);

                                    setError("");
                                    selectRuleHandler({
                                        rule,
                                        ruleType,
                                        ruleValue: {
                                            ...inputValue,
                                            endDate: current.format(),
                                        },
                                    });
                                }}
                                showTimeSelect
                                showTimeSelectOnly
                                timeIntervals={15}
                                timeCaption="Time"
                                dateFormat="HH:mm"
                                popperZIndex={5000}
                                popperProps={{ strategy: 'fixed' }}
                                onFocus={(e) => e.stopPropagation()}
                                customInput={
                                    <input
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                            width: "100%",
                                            padding: "10px",
                                            borderRadius: "6px",
                                            border: "1px solid #e0e0e0",
                                            fontSize: "13px",
                                            outline: 'none',
                                            backgroundColor: '#fafafa'
                                        }}
                                    />
                                }
                            />
                        </Box>
                    </Stack>
                </Box>

                <Button
                    variant="contained"
                    size="medium"
                    onClick={handleApply}
                    fullWidth
                    sx={{
                        mt: 1,
                        textTransform: 'none',
                        fontWeight: 'bold',
                        boxShadow: 'none',
                        '&:hover': { boxShadow: 'none' }
                    }}
                >
                    Done
                </Button>
            </Stack>
        </Box>
    );
};

export default SpecificPeriodPicker;
